"use client";

import { type ReactNode, createContext, useContext, useEffect } from "react";

import { z } from "zod";

import { OngoingSession } from "@/app/abertura-de-vagas/views/mounted-molecules/ongoing-session-modal";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import type { JobOpeningPanoramaType } from "@/types/src/backend/routes/expert/job-opening/panorama/[id].GET";

export const jobOpeningSchema = z.object({
  cargoId: z.string(),
  cargoName: z.string(),
  alias: z.string(),
  professionalExperienceTime: z.string(),
  workingModel: z.array(z.string()),
  typeOfContract: z.array(z.string()),
  contract: z.string(),
  education: z.string(),
  state: z.string(),
  city: z.string(),
  languages: z.array(z.string()),
  companyName: z.string(),
  segment: z.string(),
  confidentialJob: z.boolean(),
  hardSkills: z.array(z.string()),
  softSkills: z.array(z.string()),
  responsibilities: z.string(),
  minSalary: z.string(),
  maxSalary: z.string(),
  product: z.enum(["Sprint", "Expert"]),
});

export type JobOpeningSchema = z.infer<typeof jobOpeningSchema>;

type SeniorityWithId = Record<
  NonNullable<JobOpeningPanoramaType["level"]>,
  string
>;

export type FormDataContext = {
  seniorityIds?: SeniorityWithId;
  level: JobOpeningPanoramaType["level"];
  cargoId: string;
  cargoName: string;
  alias: string;
  professionalExperienceTime: string;
  workingModel?: string[];
  typeOfContract?: string[];
  contract?: string;
  education?: string;
  state?: string;
  city?: string;
  languages?: string[];
  companyName: string;
  segment: string;
  confidentialJob: boolean;
  hardSkills: string[];
  softSkills: string[];
  responsibilities: string;
  minSalary: number;
  maxSalary: number;
  product: "Sprint" | "Expert";
  experienceLevel?: string;
};

export const initialValues = {
  seniorityIds: {
    senior: "",
    middle: "",
    junior: "",
  },
  level: "" as JobOpeningPanoramaType["level"],
  cargoId: "",
  cargoName: "",
  alias: "",
  professionalExperienceTime: "",
  workingModel: [],
  typeOfContract: [],
  contract: "",
  education: "",
  state: "",
  city: "",
  languages: [],
  companyName: "",
  segment: "",
  confidentialJob: false,
  hardSkills: [],
  softSkills: [],
  responsibilities: "",
  minSalary: 0,
  maxSalary: 0,
  product: "" as "Sprint" | "Expert",
  experienceLevel: "",
} as FormDataContext;

interface IJobOpeningContext {
  formDataContext: FormDataContext;
  setFormDataContext: React.Dispatch<React.SetStateAction<FormDataContext>>;
}

const JobOpeningContext = createContext<IJobOpeningContext>({
  formDataContext: initialValues,
  setFormDataContext: () => null,
});

export const JobOpeningProvider = ({
  children,
  cargo,
  cargoId,
}: {
  children: ReactNode;
  cargo?: JobOpeningPanoramaType;
  cargoId?: string;
}) => {
  const [formDataContext, setFormDataContext] = useLocalStorage(
    "abertura-de-vagas-form-data",
    {
      ...initialValues,
      cargoId: cargoId ?? "",
      cargoName: cargo?.name ?? "",
      level: cargo?.level ?? ("" as JobOpeningPanoramaType["level"]),
      alias: cargo?.alias ?? "",
    },
  );
  const { values, setValues } = OngoingSession.useContext();

  useEffect(() => {
    if (cargo && cargoId) {
      const newContext = {
        ...formDataContext,
        level: cargo.level,
        cargoName: cargo.name,
        alias: cargo.alias!,
        cargoId: cargoId,
      };

      if (cargo.seniority) {
        newContext.seniorityIds = newContext.seniorityIds ?? {
          senior: "",
          middle: "",
          junior: "",
        };

        if (cargo.seniority.senior)
          newContext.seniorityIds.senior = cargo.seniority.senior.id;
        if (cargo.seniority.middle)
          newContext.seniorityIds.middle = cargo.seniority.middle.id;
        if (cargo.seniority.junior)
          newContext.seniorityIds.junior = cargo.seniority.junior.id;
      } else newContext.seniorityIds = undefined;

      setFormDataContext(newContext);
      if (
        values.cargoId === "" &&
        values.name === "" &&
        cargo?.name &&
        cargoId
      ) {
        setValues({
          ...values,
          name: cargo.alias ?? cargo.name,
          cargoId: cargoId,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargo, cargoId]);

  return (
    <JobOpeningContext.Provider value={{ formDataContext, setFormDataContext }}>
      {children}
    </JobOpeningContext.Provider>
  );
};

export function useJobOpeningContext() {
  const context = useContext(JobOpeningContext);

  if (context === undefined) {
    throw new Error(
      "useJobOpeningContext precisa estar dentro de AberturaDeVagasProvider",
    );
  }

  return useContext(JobOpeningContext);
}
